import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, ListGroup, Modal, Spinner } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import { formatChallengeRating } from '../utils/challengeRatings';
import MonsterStatBlock from './MonsterStatBlock';

export default function CustomMonsterManager({ show, onHide, onDeleted }) {
  const [monsters, setMonsters] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [deletingIndex, setDeletingIndex] = useState('');
  const deleteInFlight = useRef(false);

  useEffect(() => {
    if (!show) return undefined;
    let active = true;
    setLoading(true); setError(''); setSearch(''); setViewing(null); setConfirming(null);
    apiFetch('/monsters/custom').then(async (response) => {
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Unable to load custom monsters.');
      if (active) setMonsters(Array.isArray(result) ? result : []);
    }).catch((loadError) => { if (active) setError(loadError.message || 'Unable to load custom monsters.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [show]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return monsters.filter((monster) => !query || monster.name?.toLowerCase().includes(query));
  }, [monsters, search]);

  const remove = async () => {
    if (!confirming || deleteInFlight.current) return;
    const monster = confirming;
    deleteInFlight.current = true;
    setDeletingIndex(monster.index); setError('');
    try {
      const response = await apiFetch(`/monsters/custom/${monster.index}`, { method: 'DELETE' });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Unable to delete custom monster.');
      setMonsters((current) => current.filter((item) => item.index !== monster.index));
      if (viewing?.index === monster.index) setViewing(null);
      setConfirming(null);
      onDeleted(monster);
    } catch (deleteError) { setError(deleteError.message || 'Unable to delete custom monster.'); }
    finally { deleteInFlight.current = false; setDeletingIndex(''); }
  };

  return <>
    <Modal show={show} onHide={deletingIndex ? undefined : onHide} size="lg" scrollable centered className="dnd-modal">
      <Modal.Header closeButton={!deletingIndex}><Modal.Title>Custom Monsters</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3" controlId="custom-monster-search"><Form.Label>Search by name</Form.Label><Form.Control value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search custom monsters" /></Form.Group>
        {error && <Alert variant="danger" role="alert">{error}</Alert>}
        {loading ? <div className="text-center py-4"><Spinner role="status" /><div>Loading custom monsters…</div></div>
          : monsters.length === 0 ? <div className="text-center text-muted py-4">You have not created any custom monsters yet.</div>
          : filtered.length === 0 ? <div className="text-center text-muted py-4">No custom monsters match your search.</div>
          : <ListGroup>{filtered.map((monster) => <ListGroup.Item key={monster.index} className="d-flex flex-wrap gap-3 align-items-center bg-dark text-light border-secondary">
            {monster.tokenImage?.secureUrl ? <img src={monster.tokenImage.secureUrl} alt={`${monster.name} token`} width="56" height="56" style={{ objectFit: 'contain' }} /> : <div className="border rounded d-flex align-items-center justify-content-center text-muted" aria-label="No token image" style={{ width: 56, height: 56 }}>—</div>}
            <div className="flex-grow-1"><strong>{monster.name}</strong><div className="small text-white-50">CR {formatChallengeRating(monster.challengeRating)} • {Number(monster.xp || 0).toLocaleString()} XP</div></div>
            <Button variant="outline-light" size="sm" onClick={() => setViewing(monster)}>View</Button>
            <Button variant="outline-danger" size="sm" disabled={Boolean(deletingIndex)} onClick={() => setConfirming(monster)}>Delete</Button>
          </ListGroup.Item>)}</ListGroup>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onHide} disabled={Boolean(deletingIndex)}>Close</Button></Modal.Footer>
    </Modal>
    <Modal show={Boolean(viewing)} onHide={() => setViewing(null)} size="lg" scrollable centered className="dnd-modal"><Modal.Header closeButton><Modal.Title>{viewing?.name}</Modal.Title></Modal.Header><Modal.Body>{viewing && <MonsterStatBlock monster={viewing} />}</Modal.Body></Modal>
    <Modal show={Boolean(confirming)} onHide={deletingIndex ? undefined : () => setConfirming(null)} centered className="dnd-modal"><Modal.Header closeButton={!deletingIndex}><Modal.Title>Delete custom monster?</Modal.Title></Modal.Header><Modal.Body>Delete {confirming?.name} from your custom monsters? Enemies already added to campaigns will remain unchanged.</Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setConfirming(null)} disabled={Boolean(deletingIndex)}>Cancel</Button><Button variant="danger" onClick={remove} disabled={Boolean(deletingIndex)}>{deletingIndex && <Spinner size="sm" className="me-2" />}Delete</Button></Modal.Footer></Modal>
  </>;
}
